import sumBy from 'lodash/sumBy';
import round from 'lodash/round';
import isEqual from 'lodash/isEqual';
import {
  Form,
  BoxExpand,
  UploadInput,
  TextAreaInput,
  DeleteModal,
  TextInput,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  HoursInput,
} from '@thinknet-gmbh/platform-common-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteTask } from 'src/hooks/tasks/delete-task.hook';
import { useDeleteTaskFile } from 'src/hooks/tasks/delete-task-file.hook';
import { useGetTaskTemplatesByIds } from 'src/hooks/task-templates/get-task-templates-by-ids.hook';

import { ICreateTaskProps } from './create-task.interfaces';
import { TimeStripesList } from './components/time-stripes-list';
import { AddTaskWrapper, FormContainer, UploadWrapper } from './create-task.styles';
import { TaskExpandActions } from './components/task-expand-actions';

const CreateTask = ({
  project,
  taskTemplatesIds = [],
  fields,
  removeTask,
  addTask,
  form,
  selectedGantts,
}: ICreateTaskProps) => {
  const { t } = useTranslation(['common', 'projects']);
  const [selectedTaskTemplatesIds, setSelectedTaskTemplatesIds] =
    useState<string[]>(taskTemplatesIds);
  const [newTemplatesTasks, setNewTemplatesTasks] = useState<any[]>([]);
  const [deletedTask, setDeletedTask] = useState<{ id: string; fieldToRemove: number } | null>(
    null
  );

  const { taskTemplates, loading } = useGetTaskTemplatesByIds(selectedTaskTemplatesIds);

  const deleteTaskFile = useDeleteTaskFile();
  const deleteTask = useDeleteTask();

  const onDeleteModelSubmit = () => {
    if (deletedTask) {
      removeTask(deletedTask.fieldToRemove);
      setDeletedTask(null);
      deleteTask({ variables: { taskId: deletedTask.id } });
    }
  };

  useEffect(() => {
    !isEqual(selectedTaskTemplatesIds, taskTemplatesIds) &&
      setSelectedTaskTemplatesIds(taskTemplatesIds);
  }, [taskTemplatesIds, selectedTaskTemplatesIds]);

  useEffect(() => {
    if (taskTemplates.length && !loading) {
      setNewTemplatesTasks(taskTemplates);
    }
  }, [taskTemplates, loading]);

  useEffect(() => {
    if (newTemplatesTasks.length) {
      newTemplatesTasks.forEach((template: any) => {
        template.taskNames.forEach((taskName: string) => {
          addTask({ name: taskName, timeStripes: [{ name: '1' }] });
        });
      });

      setNewTemplatesTasks([]);
    }
  }, [newTemplatesTasks, addTask]);

  return (
    <div>
      {deletedTask && (
        <DeleteModal
          title={t('projects:modals.deleteTask.title')}
          subTitle={t('projects:modals.deleteTask.subtitle')}
          onClose={() => setDeletedTask(null)}
          onSubmit={onDeleteModelSubmit}
          isOpened
          translate={t}
        />
      )}
      {fields.map(field => {
        const task = project ? project.tasks![field.name] : undefined;

        const onFileDelete = (file: any) => {
          if (!file.originFileObj && !!task?._id) {
            deleteTaskFile({ variables: { fileId: file.uid, taskId: task._id } });
          }
        };

        return (
          <BoxExpand
            title={task ? task.name : `${t('projects:task.task')} ${field.name + 1}`}
            key={field.key}
            separator
            buttonsInTitle
            buttons={
              <TaskExpandActions
                onDelete={() => {
                  if (task) {
                    setDeletedTask({ id: task._id, fieldToRemove: field.name });
                    return;
                  }
                  removeTask(field.name);
                }}
              />
            }
            subTitle={
              task ? `${task.timeStripes.length} ${t('projects:labels.timeStripe')}` : undefined
            }
            isExpandedWhenEdit
          >
            <FormContainer>
              <div>
                <Form.Item
                  name={[field.name, 'name']}
                  label={t('projects:task.taskName')}
                  rules={[{ required: true, message: t('projects:validation.taskNameRequired') }]}
                >
                  <TextInput maxLength={50} placeholder="Paint the door" />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const timeStripes = getFieldValue(['tasks', field.name, 'timeStripes']);
                    const totalHours = sumBy(timeStripes, 'plannedHours');
                    const placeholder = totalHours
                      ? `${round(totalHours, 2)} hr`
                      : t('projects:labels.plannedHoursPlaceholder');

                    return (
                      <Form.Item
                        name="taskPlannedHours"
                        label={t('projects:labels.taskPlannedHours')}
                      >
                        <HoursInput disabled placeholder={placeholder} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </div>

              <Form.Item name={[field.name, 'notes']} label={t('projects:task.note')}>
                <TextAreaInput
                  showCount
                  maxLength={2000}
                  placeholder={t('projects:task.writeSomeNotes')}
                />
              </Form.Item>
              <UploadWrapper>
                <Form.Item name={[field.name, 'files']} valuePropName="defaultFileList">
                  <UploadInput
                    variant="area"
                    beforeUpload={() => false}
                    btnText={t('projects:task.uploadedFile')}
                    onRemove={onFileDelete}
                  />
                </Form.Item>
              </UploadWrapper>
            </FormContainer>
            <TimeStripesList
              field={field}
              isDisabled={Boolean(task)}
              form={form}
              selectedGantts={selectedGantts}
            />
          </BoxExpand>
        );
      })}
      <AddTaskWrapper>
        <Button
          icon={ICONS.plus}
          type="primary"
          ghost
          iconColor={PLATFORM_THEME_COLORS.primary.blue}
          onClick={() => {
            const projectName = form.getFieldValue('name');
            const ganttIds = form.getFieldValue('ganttIds') || [];
            const initialTimeStripe: any = { name: '1' };

            if (ganttIds.length === 1) {
              initialTimeStripe.ganttId = ganttIds[0];
            }

            if (projectName) {
              initialTimeStripe.name = '1';
              initialTimeStripe.createdAt = new Date();
            }

            addTask({ timeStripes: [initialTimeStripe] });
          }}
        >
          {t('projects:buttons.addNewTask')}
        </Button>
      </AddTaskWrapper>
    </div>
  );
};

export default CreateTask;
